import { useCallback, useEffect, useMemo, useState } from 'react';
import { STATUS_LABEL } from './ui';
import { CLASS_LIST, defaultPaper, fromServer, numbered, paperTotal } from './paperUtils';
import * as api from './paperApi';

const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// All state + actions for the builder; the component stays presentational.
export default function useBuilder(user, isPrincipal) {
  const [view, setView] = useState('papers'); // papers | editor | master
  const [tab, setTab] = useState('setup'); // setup | sections | ingest
  const [paper, setPaper] = useState(null);
  const [paperId, setPaperId] = useState(null);
  const [meta, setMeta] = useState({ status: 'draft', readyForPrint: false });
  const [scope, setScope] = useState(null);
  const [master, setMaster] = useState(null);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [topics, setTopics] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [busyId, setBusyId] = useState('');
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.getScope(user).then(setScope).catch(() => setScope({ all: isPrincipal, classes: [], subjects: [] }));
    api.getMasterHeader(user).then(setMaster).catch(() => {});
    api.getStatus(user).then((s) => setAiConfigured(!!s.aiConfigured)).catch(() => {});
  }, [user, isPrincipal]);

  const flash = (type, text) => setMsg({ type, text });
  const setSections = useCallback((upd) => setPaper((p) => ({ ...p, sections: typeof upd === 'function' ? upd(p.sections) : upd })), []);

  const newPaper = () => {
    const p = defaultPaper(master, user);
    if (scope && !scope.all) {
      const cls = scope.classes.length === 1 ? CLASS_LIST.find((x) => x.toLowerCase() === scope.classes[0]) : '';
      if (cls) p.classId = cls;
      if (scope.subjects.length === 1) p.subject = titleCase(scope.subjects[0]);
    }
    setPaper(p); setPaperId(null); setMeta({ status: 'draft', readyForPrint: false });
    flash('', ''); setTab('setup'); setView('editor');
  };

  const openPaper = async (id) => {
    try {
      const p = await api.getPaper(user, id);
      setPaper(fromServer(p)); setPaperId(p._id); setMeta({ status: p.status, readyForPrint: p.readyForPrint });
      flash('', ''); setTab('sections'); setView('editor');
    } catch (e) { flash('err', api.errMsg(e)); }
  };

  const autoTotal = useMemo(() => (paper ? paperTotal(paper.sections) : 0), [paper]);
  const mismatch = !!paper?.maxMarksLocked && autoTotal !== Number(paper.maxMarks);
  const locked = !isPrincipal && meta.status === 'approved';

  const save = async (status) => {
    if (!paper.title.trim()) return flash('err', 'Enter a paper title in Setup.');
    if (!paper.classId || !paper.subject) return flash('err', 'Select class and subject in Setup.');
    if (!autoTotal) return flash('err', 'Add at least one question with marks.');
    if (status === 'pending_approval' && mismatch) return flash('err', `Question marks (${autoTotal}) do not match the locked maximum (${paper.maxMarks}).`);
    setSaving(true);
    const body = { ...paper, sections: numbered(paper.sections), maxMarks: paper.maxMarksLocked ? Number(paper.maxMarks) : autoTotal, status, readyForPrint: meta.readyForPrint };
    try {
      const saved = paperId ? await api.updatePaper(user, paperId, body) : await api.createPaper(user, body);
      setPaperId(saved._id); setMeta({ status: saved.status, readyForPrint: saved.readyForPrint });
      setRefresh((r) => r + 1);
      flash('ok', `Saved as ${STATUS_LABEL[saved.status]}${saved.readyForPrint ? ' · Ready for print' : ''}.`);
    } catch (e) { flash('err', api.errMsg(e)); }
    setSaving(false);
  };

  const approve = async (on) => {
    if (!paperId) return flash('err', 'Save the paper first.');
    try {
      const r = await api.setPaperStatus(user, paperId, { status: on ? 'approved' : 'draft' });
      setMeta({ status: r.status, readyForPrint: r.readyForPrint }); setRefresh((x) => x + 1);
      flash('ok', on ? 'Approved for exam.' : 'Approval revoked.');
    } catch (e) { flash('err', api.errMsg(e)); }
  };

  const toggleReady = (on) => {
    setMeta((m) => ({ ...m, readyForPrint: on }));
    if (paperId) api.setPaperStatus(user, paperId, { readyForPrint: on }).then(() => setRefresh((x) => x + 1)).catch((e) => flash('err', api.errMsg(e)));
  };

  const regen = async (sid, q, modifier) => {
    setBusyId(q.id);
    try {
      const r = await api.regenerateQuestion(user, {
        classId: paper.classId, subject: paper.subject, topics, difficulty: q.difficulty || difficulty,
        type: q.type, marks: q.marks, currentText: q.text, modifier
      });
      const nd = modifier === 'harder' ? 'hard' : modifier === 'simpler' ? 'easy' : (q.difficulty || r.difficulty);
      setSections((ss) => ss.map((s) => (s.id !== sid ? s
        : { ...s, questions: s.questions.map((x) => (x.id === q.id ? { ...x, ...r, id: q.id, difficulty: nd } : x)) })));
    } catch (e) { flash('err', api.errMsg(e)); }
    setBusyId('');
  };

  return {
    view, setView, tab, setTab, paper, setPaper, paperId, meta, scope, setMaster, aiConfigured, topics, setTopics,
    difficulty, setDifficulty, msg, flash, busyId, saving, refresh, setSections, newPaper, openPaper,
    autoTotal, mismatch, locked, save, approve, toggleReady, regen
  };
}
